import React, { useState, useEffect } from 'react';
import API from '../api';
import { trackSignup } from '../analytics';
import SupportForm from '../components/SupportForm';

export default function LoginPage({ onLogin }) {
  // A reset link lands on /?reset=<token>. There's no router in this app, so the
  // token in the query string is what puts the page into 'reset' mode.
  // Ad and invite links land on /?join so new visitors start on CREATE ACCOUNT
  // instead of a sign-in form they can't use yet.
  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('reset');
  const [mode, setMode] = useState(resetToken ? 'reset' : urlParams.has('join') ? 'register' : 'login');
  const [form, setForm] = useState({ username: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenState, setTokenState] = useState(resetToken ? 'checking' : null);
  const [resetFor, setResetFor] = useState('');

  // Check the link before asking for a password, so an expired one says so up
  // front instead of after they've typed a new password twice.
  useEffect(() => {
    if (!resetToken) return;
    let alive = true;
    API.get(`/auth/reset-password/${encodeURIComponent(resetToken)}`)
      .then(r => { if (alive) { setTokenState('valid'); setResetFor(r.data.username || ''); } })
      .catch(err => {
        if (!alive) return;
        setTokenState('invalid');
        setError(err.response?.data?.error || 'This reset link has expired or already been used.');
      });
    return () => { alive = false; };
  }, [resetToken]);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  /** Drop ?reset= so a refresh doesn't drag them back into a spent link. */
  const clearResetParam = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('reset');
    window.history.replaceState({}, '', url.pathname + url.search);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setNotice('');
    setLoading(true);
    try {
      if (mode === 'forgot') {
        const res = await API.post('/auth/forgot-password', { email: form.username });
        setNotice(res.data?.message || 'Check your email for a reset link.');
        setLoading(false);
        return;
      }
      if (mode === 'reset') {
        if (form.password !== form.confirm) {
          setError("Those two passwords don't match.");
          setLoading(false);
          return;
        }
        await API.post('/auth/reset-password', { token: resetToken, password: form.password });
        clearResetParam();
        setMode('login');
        setForm({ username: '', email: '', password: '', confirm: '' });
        setNotice('Password updated. Sign in with your new one.');
        setLoading(false);
        return;
      }
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const payload = mode === 'login'
        ? { username: form.username, password: form.password }
        : { username: form.username, email: form.email, password: form.password };
      const res = await API.post(endpoint, payload);
      if (mode === 'register') trackSignup();
      onLogin({ username: res.data.username, is_admin: res.data.is_admin }, res.data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isMobile = window.innerWidth < 768;

  return (
    <div style={{ minHeight:'100vh', display:'flex', flexDirection: isMobile ? 'column' : 'row', background:'#0a0e1a', overflow:'hidden' }}>
      <style>{`
        @keyframes floatBall {
          0%,100% { transform: translateY(0px) rotate(-8deg); }
          50%      { transform: translateY(-18px) rotate(8deg); }
        }
        @keyframes fadeUp {
          from { opacity:0; transform:translateY(24px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .login-input:focus { border-color: rgba(213,10,10,0.6) !important; }
        .login-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 24px rgba(213,10,10,0.5); }
        .mode-tab:hover { color: #ccd; }
      `}</style>

      {/* ── Left hero panel ── */}
      <div style={{
        flex: isMobile ? 'none' : '1.1',
        background: 'linear-gradient(160deg, #013369 0%, #001830 60%, #0a0e1a 100%)',
        display: 'flex', flexDirection: 'column',
        alignItems: isMobile ? 'center' : 'flex-start',
        justifyContent: 'center',
        padding: isMobile ? '48px 28px 36px' : '60px 56px',
        position: 'relative', overflow: 'hidden',
        borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.06)',
        borderBottom: isMobile ? '1px solid rgba(255,255,255,0.06)' : 'none',
      }}>
        {/* Background decoration */}
        <div style={{ position:'absolute', top:-80, right:-80, width:320, height:320, borderRadius:'50%', background:'rgba(213,10,10,0.06)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:-60, left:-60, width:240, height:240, borderRadius:'50%', background:'rgba(1,51,105,0.15)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:'50%', right: isMobile ? '5%' : '8%', transform:'translateY(-50%)', opacity:0.06, fontSize:220, pointerEvents:'none', userSelect:'none' }}>🏈</div>

        {/* NFL badge */}
        <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(213,10,10,0.15)', border:'1px solid rgba(213,10,10,0.3)', borderRadius:20, padding:'6px 14px', marginBottom: isMobile ? 20 : 28, animation:'fadeUp 0.5s ease-out' }}>
          <span style={{ fontSize:16 }}>🏈</span>
          <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:12, fontWeight:700, color:'#ff8888', letterSpacing:2 }}>RIVALRY PICKS</span>
        </div>

        {/* Hero headline */}
        <div style={{ animation:'fadeUp 0.6s ease-out 0.1s both' }}>
          <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:700, color:'#fff', lineHeight:1, textAlign: isMobile ? 'center' : 'left', marginBottom:4 }}>
            <span style={{ fontSize: isMobile ? 44 : 58, display:'block' }}>MAKE YOUR</span>
            <span style={{ fontSize: isMobile ? 44 : 58, color:'#D50A0A', display:'block' }}>PICKS.</span>
            <span style={{ fontSize: isMobile ? 44 : 58, display:'block', marginTop:4 }}>BEAT YOUR</span>
            <span style={{ fontSize: isMobile ? 44 : 58, color:'#D50A0A', display:'block' }}>FRIENDS.</span>
          </div>
        </div>

        {/* Subline */}
        <div style={{ marginTop: isMobile ? 16 : 24, color:'#556', fontSize:15, lineHeight:1.6, textAlign: isMobile ? 'center' : 'left', maxWidth:340, animation:'fadeUp 0.6s ease-out 0.2s both' }}>
          Pick every NFL game each week. The most correct picks wins. Trash talk included.
        </div>

        {/* Price + trust block — the three things ad traffic lands here to confirm */}
        <div style={{ marginTop: isMobile ? 18 : 26, animation:'fadeUp 0.6s ease-out 0.25s both' }}>
          <div style={{ fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize: isMobile ? 20 : 24, color:'#fff', letterSpacing:0.5, textAlign: isMobile ? 'center' : 'left' }}>
            <span style={{ color:'#D50A0A' }}>$10</span> A HEAD. THE WHOLE SEASON.
          </div>
          <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:6, alignItems: isMobile ? 'center' : 'flex-start' }}>
            {[
              'Week 1 through the Super Bowl',
              "Nothing to download, it works from a link",
              "We never touch your pool's money",
            ].map(line => (
              <div key={line} style={{ display:'flex', alignItems:'center', gap:8, color:'#778', fontSize:13.5 }}>
                <span style={{ color:'#4caf50', fontWeight:700 }}>✓</span>{line}
              </div>
            ))}
          </div>
        </div>

        {/* Feature pills */}
        {!isMobile && (
          <div style={{ display:'flex', gap:8, marginTop:32, flexWrap:'wrap', animation:'fadeUp 0.6s ease-out 0.3s both' }}>
            {['Live scores','Pool standings','Survivor mode','Trash talk'].map(f => (
              <span key={f} style={{ padding:'5px 14px', borderRadius:20, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', color:'#778', fontSize:12, fontWeight:600 }}>{f}</span>
            ))}
          </div>
        )}
      </div>

      {/* ── Right form panel ── */}
      <div style={{
        flex: isMobile ? 'none' : '0.9',
        display:'flex', alignItems:'center', justifyContent:'center',
        padding: isMobile ? '36px 24px 48px' : '40px 48px',
        background:'#0a0e1a',
      }}>
        <div style={{ width:'100%', maxWidth:380, animation:'fadeUp 0.5s ease-out 0.15s both' }}>
          {/* Mode tabs — irrelevant while following a reset link or asking for one */}
          {(mode === 'login' || mode === 'register') && (
          <div style={{ display:'flex', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:4, marginBottom:28 }}>
            {['login','register'].map(m => (
              <button
                key={m}
                className="mode-tab"
                onClick={() => { setMode(m); setError(''); setNotice(''); }}
                style={{ flex:1, padding:'11px 0', border:'none', borderRadius:7, cursor:'pointer', fontSize:14, fontWeight:700, transition:'all 0.2s', background: mode === m ? '#D50A0A' : 'transparent', color: mode === m ? '#fff' : '#556', letterSpacing:0.5, fontFamily:"'Oswald',sans-serif" }}
              >{m === 'login' ? 'SIGN IN' : 'CREATE ACCOUNT'}</button>
            ))}
          </div>
          )}

          {mode === 'contact' && (
            <div style={{ marginBottom:24 }}>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:22, fontWeight:700, color:'#fff', marginBottom:6 }}>
                TELL US WHAT'S WRONG
              </div>
              <div style={{ fontSize:13.5, color:'#667', lineHeight:1.55 }}>
                Goes straight to the person who built this. Leave an email you can
                actually read and you'll get an answer, usually the same day.
              </div>
            </div>
          )}

          {(mode === 'forgot' || mode === 'reset') && (
            <div style={{ marginBottom:24 }}>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:22, fontWeight:700, color:'#fff', marginBottom:6 }}>
                {mode === 'forgot' ? 'FORGOT YOUR PASSWORD?' : 'SET A NEW PASSWORD'}
              </div>
              <div style={{ fontSize:13.5, color:'#667', lineHeight:1.55 }}>
                {mode === 'forgot'
                  ? "Enter your username or email and we'll send you a link to set a new one."
                  : resetFor
                    ? `Signing back in as ${resetFor}. Pick something you'll remember.`
                    : "Pick something you'll remember."}
              </div>
            </div>
          )}

          {error && (
            <div style={{ background:'rgba(213,10,10,0.12)', border:'1px solid rgba(213,10,10,0.3)', borderRadius:8, padding:'10px 14px', color:'#ff8888', fontSize:13, marginBottom:20, textAlign:'center' }}>
              {error}
            </div>
          )}
          {notice && (
            <div style={{ background:'rgba(76,175,80,0.10)', border:'1px solid rgba(76,175,80,0.35)', borderRadius:8, padding:'10px 14px', color:'#81c784', fontSize:13, marginBottom:20, textAlign:'center' }}>
              {notice}
            </div>
          )}

          {/* The only channel that works for somebody who can't get in.
              Everything else on this page assumes the account is reachable: the
              reset link needs a working email on file, and a DM needs an account
              to send it from. This needs neither. */}
          {mode === 'contact' && (
            <div style={{ marginBottom: 4 }}>
              {/* compact: the heading above already says what this is for, and
                  saying it twice reads like filler. */}
              <SupportForm compact />
            </div>
          )}

          {mode !== 'contact' && (
          <form onSubmit={submit}>
            {/* Signing in has always accepted either the username or the email
                address — the lookup checks both — but the form only ever said
                "username", so nobody knew. */}
            {mode !== 'reset' && (
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#667', marginBottom:7, letterSpacing:1 }}>
                {mode === 'register' ? 'USERNAME' : 'USERNAME OR EMAIL'}
              </label>
              <input
                className="login-input"
                style={{ width:'100%', padding:'13px 16px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#fff', fontSize:15, outline:'none', transition:'border-color 0.2s', boxSizing:'border-box' }}
                name="username" value={form.username} onChange={handle}
                placeholder={mode === 'register' ? 'Choose a username' : 'Username or email address'}
                autoComplete="username" required
              />
              {(mode === 'login' || mode === 'forgot') && (
                <div style={{ fontSize:11.5, color:'#556', marginTop:6 }}>
                  Either works, whichever you remember.
                </div>
              )}
            </div>
            )}

            {mode === 'register' && (
              <div style={{ marginBottom:16 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#667', marginBottom:7, letterSpacing:1 }}>EMAIL</label>
                <input
                  className="login-input"
                  style={{ width:'100%', padding:'13px 16px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#fff', fontSize:15, outline:'none', transition:'border-color 0.2s', boxSizing:'border-box' }}
                  name="email" type="email" value={form.email} onChange={handle}
                  placeholder="your@email.com" required
                />
              </div>
            )}

            {mode !== 'forgot' && (
            <div style={{ marginBottom: mode === 'reset' ? 16 : 24 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#667', marginBottom:7, letterSpacing:1 }}>
                {mode === 'reset' ? 'NEW PASSWORD' : 'PASSWORD'}
              </label>
              <input
                className="login-input"
                style={{ width:'100%', padding:'13px 16px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#fff', fontSize:15, outline:'none', transition:'border-color 0.2s', boxSizing:'border-box' }}
                name="password" type="password" value={form.password} onChange={handle}
                placeholder={mode === 'login' ? 'Enter your password' : 'Min 8 characters'}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required
              />
            </div>
            )}

            {mode === 'reset' && (
              <div style={{ marginBottom:24 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#667', marginBottom:7, letterSpacing:1 }}>CONFIRM NEW PASSWORD</label>
                <input
                  className="login-input"
                  style={{ width:'100%', padding:'13px 16px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#fff', fontSize:15, outline:'none', transition:'border-color 0.2s', boxSizing:'border-box' }}
                  name="confirm" type="password" value={form.confirm} onChange={handle}
                  placeholder="Type it again" autoComplete="new-password" required
                />
              </div>
            )}

            <button
              className="login-btn"
              style={{ width:'100%', padding:'15px', background:'linear-gradient(135deg,#D50A0A,#a00808)', border:'none', borderRadius:8, color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:"'Oswald',sans-serif", letterSpacing:1, transition:'all 0.2s', boxShadow:'0 4px 16px rgba(213,10,10,0.35)', opacity: loading ? 0.7 : 1 }}
              type="submit" disabled={loading || (mode === 'reset' && tokenState !== 'valid')}
            >
              {loading ? 'Loading...'
                : mode === 'login' ? 'SIGN IN'
                : mode === 'register' ? 'CREATE ACCOUNT'
                : mode === 'forgot' ? 'EMAIL ME A RESET LINK'
                : tokenState === 'checking' ? 'CHECKING LINK…'
                : tokenState === 'invalid' ? 'LINK EXPIRED'
                : 'SET NEW PASSWORD'}
            </button>
          </form>
          )}

          {mode === 'login' && (
            <div style={{ textAlign:'center', marginTop:14 }}>
              <button
                onClick={() => { setMode('forgot'); setError(''); setNotice(''); }}
                style={{ background:'none', border:'none', color:'#7fb3ff', fontSize:13, cursor:'pointer', fontWeight:600 }}
              >Forgot your password?</button>
            </div>
          )}

          {(mode === 'forgot' || (mode === 'reset' && tokenState === 'invalid')) && (
            <div style={{ textAlign:'center', marginTop:14 }}>
              <button
                onClick={() => { clearResetParam(); setMode(mode === 'forgot' ? 'login' : 'forgot'); setError(''); setNotice(''); }}
                style={{ background:'none', border:'none', color:'#7fb3ff', fontSize:13, cursor:'pointer', fontWeight:600 }}
              >{mode === 'forgot' ? '← Back to sign in' : 'Send me a new link'}</button>
            </div>
          )}

          {mode === 'register' && (
            <p style={{ color:'#445', fontSize:12, textAlign:'center', marginTop:16 }}>
              Takes about a minute, then join your pool with an invite code
            </p>
          )}

          {(mode === 'login' || mode === 'register') && (
          <div style={{ marginTop:20, textAlign:'center' }}>
            <span style={{ color:'#445', fontSize:13 }}>
              {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
            </span>
            <button
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
              style={{ background:'none', border:'none', color:'#7fb3ff', fontSize:13, cursor:'pointer', fontWeight:600 }}
            >
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </div>
          )}

          {/* Deliberately on every mode except itself, including the reset
              screens. Somebody whose reset link expired twice has already tried
              the self-serve path and needs a person, not another link. */}
          {mode !== 'contact' && (
            <div style={{ textAlign:'center', marginTop:18, paddingTop:16, borderTop:'1px solid rgba(255,255,255,0.07)' }}>
              <button
                onClick={() => { setMode('contact'); setError(''); setNotice(''); }}
                style={{ background:'none', border:'none', color:'#667', fontSize:12.5, cursor:'pointer', fontWeight:600 }}
              >Can't get in, or something's broken? Message us</button>
            </div>
          )}

          {mode === 'contact' && (
            <div style={{ textAlign:'center', marginTop:14 }}>
              <button
                onClick={() => { clearResetParam(); setMode('login'); setError(''); setNotice(''); }}
                style={{ background:'none', border:'none', color:'#7fb3ff', fontSize:13, cursor:'pointer', fontWeight:600 }}
              >← Back to sign in</button>
            </div>
          )}

          {/* Real links, not app screens. Somebody deciding whether to hand over
              ten dollars, and anyone reviewing the business later, both need to
              read these without an account. */}
          {/* Bigger and above the small print, because the people who need it
              are the ones least likely to go hunting for a link. It has to be
              reachable from out here: this is the screen you are stuck on when
              you cannot work out how to get in. */}
          <div style={{ textAlign:'center', marginTop:18 }}>
            <a
              href="/help"
              style={{
                display:'inline-block', color:'#7fb3ff', fontSize:13.5, fontWeight:700,
                textDecoration:'none', padding:'10px 16px', borderRadius:8,
                border:'1px solid rgba(127,179,255,0.35)', background:'rgba(127,179,255,0.07)'
              }}
            >📖 New here? How to use the app</a>
          </div>

          <div style={{ textAlign:'center', marginTop:14, color:'#445', fontSize:12 }}>
            <a href="/privacy" style={{ color:'#556', textDecoration:'none' }}>Privacy</a>
            <span style={{ margin:'0 8px' }}>·</span>
            <a href="/terms" style={{ color:'#556', textDecoration:'none' }}>Terms</a>
          </div>
        </div>
      </div>
    </div>
  );
}
